import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  auth,
  logOut,
} from "../firebase/firestore.utils";
import {
  getAuthErrorMessage,
  isActiveProfile,
  isAdministrationProfile,
  isDispatcherOrAdministrationProfile,
  signOutInactiveUser,
  validateActiveUser,
} from "../firebase/auth.utils";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authUser, setAuthUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const clearSession = useCallback(() => {
    setAuthUser(null);
    setUserProfile(null);
  }, []);

  const resolveAuthUser = useCallback(
    async (firebaseUser) => {
      if (!firebaseUser) {
        clearSession();
        setAuthError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const validation = await validateActiveUser(firebaseUser);

      if (!validation.ok) {
        await signOutInactiveUser();
        clearSession();
        setAuthError(getAuthErrorMessage(validation.reason));
        setIsLoading(false);
        return;
      }

      setAuthUser(firebaseUser);
      setUserProfile(validation.profile);
      setAuthError(null);
      setIsLoading(false);
    },
    [clearSession],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      resolveAuthUser(firebaseUser);
    });
    return unsubscribe;
  }, [resolveAuthUser]);

  const refreshSession = useCallback(async () => {
    await resolveAuthUser(auth.currentUser);
  }, [resolveAuthUser]);

  const handleLogOut = useCallback(async () => {
    await logOut();
    clearSession();
    setAuthError(null);
  }, [clearSession]);

  const value = useMemo(
    () => ({
      authUser,
      userProfile,
      isLoading,
      authError,
      isActiveUser: Boolean(authUser && isActiveProfile(userProfile)),
      isAdmin: Boolean(isAdministrationProfile(userProfile)),
      isDispatcherOrAdministration: Boolean(
        isDispatcherOrAdministrationProfile(userProfile),
      ),
      refreshSession,
      logOut: handleLogOut,
      clearAuthError: () => setAuthError(null),
    }),
    [
      authUser,
      userProfile,
      isLoading,
      authError,
      refreshSession,
      handleLogOut,
    ],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return context;
};

export default AuthContext;
